import './StudentItem.scss';
import {
  COLUMN_MAPPING,
  DESCRIPTION_COLUMN,
  ReportRows,
  StudentItemProps,
  StudentItemResponse,
  downloadStudentItemReport,
  fetchStudentItemWithRetry,
} from './models';
import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { Admin, AdminStudent } from '../../shared';
import { genDDMenuItemLabel } from '../QueryPanel/models';
import { isType } from '../../shared';
import Spinner from '../Spinner/Spinner';
import { useSelector } from 'react-redux';
import { selectControls } from '../../redux/controls';
import gaEvents from '../../shared/analyticsEvents';
import { ResultFragment } from './ResultFragment';
import { useNavigate } from 'react-router';

const staticMenuStyle = {
  width: '100%',
};

export const StudentItem = ({
  admin,
  students,
  showReportSelectors,
  fromQuiz,
  setReportOnScreen,
  printSignal,
  exportSignal,
  origUrl,
}: StudentItemProps): JSX.Element => {
  const controls = useSelector(selectControls);
  const navigate = useNavigate();
  const [abortController, setAbortController] = useState<AbortController>();
  // const [reportNotReady, setReportNotReady] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<AdminStudent | null>(
    null
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const [studentItem, setStudentItem] =
    useState<ReportRows[] | null>(null);
  const [noStudentItem, setNoStudentItem] =
    useState<boolean>(false);

  const [assessmentIsStillInProgress, setAssessmentIsStillInProgress] =
    useState<boolean>(false);

  const onClickStudent = useCallback((student: AdminStudent) => {
    setSelectedStudent(student);
  }, []);

  const onReturnToTakeNaepNow = useCallback((event: SyntheticEvent) => {
    event.preventDefault();
    setSelectedStudent(null);
    setStudentItem(null);
    setNoStudentItem(true);
    // navigate to home page
    navigate('/popquiz');
  }, [navigate]);

  useEffect(() => {
    console.log(window.location.href);
  }, []);

  useEffect(() => {
    if (printSignal && !isPrinting) {
      setIsPrinting(true);
      const printableDiv = document.getElementById('student-item-section');
      if (printableDiv) {
        window.print();
      }
      setIsPrinting(false);
    }
  }, [isPrinting, printSignal]);

  useEffect(() => {
    if (exportSignal && !isExporting && studentItem !== null) {
      setIsExporting(true);
      downloadStudentItemReport(studentItem, exportSignal).finally(() => {
        setIsExporting(false);
      }).catch((error) => {
        console.error('Failed to download student item report:', error);
        setIsExporting(false);
      });
    }
  }, [exportSignal, isExporting, studentItem]);

  const onClickCreate = useCallback(
    (admin: Admin, student: AdminStudent | null): void => {
      if (!student) {
        return;
      }

      const processStudentItem = async () => {
        setIsLoading(true);
        const controller = new AbortController();
        setAbortController(controller);
        const response = await fetchStudentItemWithRetry(
          admin,
          student,
          fromQuiz,
          controller
        );
        if (
          isType<StudentItemResponse>(
            response,
            'StudentItemResponse'
          )
        ) {
          if (response.studentItem.reportDataExists) {
            setStudentItem(response.studentItem.reportRows);
            setNoStudentItem(false);
            // setReportNotReady(false);
          } else if (response.studentItem.resultsNotReady) {
            // setReportNotReady(true);
            setNoStudentItem(false);
            setAssessmentIsStillInProgress(true);
            setStudentItem(null);
          } else {
            setNoStudentItem(true);
            // setReportNotReady(false);
            setStudentItem(null);
          }
        } else {
          setNoStudentItem(true);
          // setReportNotReady(false);
          setStudentItem(null);
        }
        setIsLoading(false);
      };

      if (
        !studentItem ||
        !(
          studentItem[0].headerInfo['Student: '] ===
          student.testTakerName
        )
      ) {
        processStudentItem();
        gaEvents.onGA4CreateStudentReport();
      }
    },
    [fromQuiz, studentItem]
  );

  useEffect(() => {
    return () => {
      abortController?.abort();
    };
  }, [abortController]);

  // selects first student on mount
  useEffect(() => {
    setSelectedStudent(students[0]);
  }, [students]);

  // toggles print disabled flag
  useEffect(() => {
    if (studentItem) {
      setReportOnScreen(true);
    } else {
      setReportOnScreen(false);
    }

    return () => {
      setReportOnScreen(false);
    };
  }, [setReportOnScreen, studentItem]);

  const onAutoCreate = useCallback(
    (student: AdminStudent) => {
      if (student) {
        onClickCreate(admin, student);
      }
    },
    [admin, onClickCreate]
  );

  useEffect(() => {
    if (
      !showReportSelectors &&
      selectedStudent !== null &&
      !studentItem
    ) {
      onAutoCreate(selectedStudent);
    }
  }, [showReportSelectors, selectedStudent, studentItem, onAutoCreate]);

  return (
    <section
      className="si-container section-to-print"
      id="student-item-section"
    >
      {!showReportSelectors && (
        <div className="si-spinner-container d-flex justify-content-center align-items-center">
          { isLoading && <Spinner size={120} containerClassName="si-spinner-container" />}
        </div>
      )}
      {showReportSelectors && (
        <>
          <div className="si-first-row hide-section pt-3">
            <div className="si-student-container">
              <div className="sid--student-header w-100">
                {controls?.Rpt_StudentItem_Student?.controlText}
              </div>
              <div className="sid--student-header">
                {controls?.Rpt_StudentItemDetailStudentSel?.controlText}
              </div>
              <div className="dropdown w-100">
                <button
                  className="nqt-dropdown-btn btn dropdown-toggle"
                  type="button"
                  id="dropdownAdminStudents"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <span className="dropdown-interior">
                    {selectedStudent ? selectedStudent.testTakerName : ''}
                  </span>
                  <span className="dropdown-arrow"></span>
                </button>
                <ul
                  className="dropdown-menu"
                  style={staticMenuStyle}
                  aria-labelledby="dropdownAdminStudents"
                >
                  {genDDMenuItemLabel('Select Student')}
                  {students.length > 0 &&
                  students.map((student, ind) => (
                    <li key={`tr-rpt-${ind}`}>
                      <a
                        className={'dropdown-item' +
                          (student.testTakerID === selectedStudent?.testTakerID
                            ? ' selected'
                            : '')}
                        onClick={(e) => {
                          e.preventDefault();
                          onClickStudent(student);
                        } }
                        href="#"
                      >
                        {student.testTakerName}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="sid-create-btn-container">
              <button
                className="nqt-btn si-create-btn"
                disabled={!selectedStudent}
                onClick={() => onClickCreate(admin, selectedStudent)}
              >
                {controls?.Rpt_CreateReport?.controlText}
              </button>
            </div>
          </div>
          {isLoading &&
            <div className="si-spinner-container d-flex justify-content-center align-items-center">
              <Spinner size={120} containerClassName="si-spinner-container" />
            </div>
          }
        </>
      )}
      {/* {reportNotReady && !isLoading && (
        <div className="sid-error-message">
          <p className="error-message">
            report not ready.
          </p>

        </div>
      )} */}
      {assessmentIsStillInProgress && !isLoading && (
        <div className="sid-error-message">
          <p className="error-message">
            {controls?.Rpt_ReportInProgress?.controlText}
          </p>
          <p>
            <a className="refresh-link" href={origUrl}  >
              {origUrl}
            </a>
          </p>
        </div>
      )}
      {noStudentItem && !isLoading && (
        <div className="sid-error-message">
          <p className="error-message">
            {controls?.Rpt_ReportNoData?.controlText}
          </p>
        </div>)}
      {noStudentItem && fromQuiz && (
        <div className="si-section-return-btn">
          <button className='nqt-btn si-return-btn' onClick={(event) => onReturnToTakeNaepNow(event)}>
              Return to Take NAEP Now
          </button>
        </div>
      )}
      {studentItem !== null && !isLoading && studentItem.map((rpt, idx) => (
        <div  key={`report-${idx}`}>
          <div className="sid-second-row mb-2">
            <div className="sid-blue-header">
              {controls?.Rpt_StudentItem_Label
                ? controls.Rpt_StudentItem_Label.controlText
                : '? Report'}
            </div>
            <span className="sid-blue-header-container">
              <div className="sid-blue-header">
                {controls?.Rpt_StudentItemDetailAssessmentHdr?.controlText}
              </div>
              <span className="sid-header-label">
                {rpt.headerInfo['Administration: ']}
              </span>
            </span>
            { !fromQuiz && (<span className="sid-blue-header-container">
              <div className="sid-blue-header">
                {controls?.Rpt_StudentItemDetailStudentHdr?.controlText}
              </div>
              <span className="sid-header-label">
                {rpt.headerInfo['Student: ']}
              </span>
            </span> )}
          </div>
          {rpt.rows.length > 0 && (
            <div className="sid-table-container">
              <table className="sid-table">
                <thead>
                  <tr className="nqt-table-header-row">
                    {rpt.rows
                      .filter((row) => row.rowType === 0)
                      .map((row, index) => {
                        return row.rowCells.map((cell, ind) => (
                          <td
                            className={`nqt-table-header-cell  ${
                              COLUMN_MAPPING[ind] !== DESCRIPTION_COLUMN &&
                              'text-center align-middle'
                            }`}
                            key={`table-header-${index}-${ind}`}
                            colSpan={cell.colSpan}
                            rowSpan={cell.rowSpan}
                          >
                            {cell.stringValue}
                          </td>
                        ));
                      })}
                  </tr>
                </thead>
                <tbody>
                  {rpt.rows
                    .filter((row) => row.rowType === 1)
                    .map((row, index) => {
                      return (
                        <tr
                          className="nqt-table-body-row"
                          key={`table-body-row-${index}`}
                        >
                          {row.rowCells.map((cell, ind) => (
                            <td
                              className={`nqt-table-body-cell ${
                                COLUMN_MAPPING[ind] !== DESCRIPTION_COLUMN &&
                                'text-center align-middle'
                              }`}
                              key={`table-body-row-cell-${index}-${ind}`}
                              colSpan={cell.colSpan}
                              rowSpan={cell.rowSpan}
                            >
                              <ResultFragment
                                colIndex={ind}
                                result={cell.stringValue}
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
          <div className="si-notes-container">
            {rpt.notes.map((note, index) => (
              <p key={`note-${index}`} className="sid-notes-text">
                {note}
              </p>
            ))}
          </div>
          <div className="si-notes-container">
            {rpt.footnotes && rpt.footnotes.length > 0 && rpt.footnotes?.map((note, index) => (
              <p key={`fnote-${index}`} className="sid-notes-text">
                {note.FootnoteText}
              </p>
            ))}
          </div>
          { (fromQuiz) &&
            <div className="si-section-return-btn">
              <button className='nqt-btn si-return-btn' onClick={(event) => onReturnToTakeNaepNow(event)}>
                Return to Take NAEP Now
              </button>
            </div>
          }
        </div>
      ))}
    </section>
  );
};

export default StudentItem;
